<script>
const FILTER_KEYS = {
  ALL: 'all',
  MARKETING: 'MARKETING',
  UTILITY: 'UTILITY',
  AUTHENTICATION: 'AUTHENTICATION',
  SAMPLES: 'samples',
};

export default {
  props: {
    inboxId: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      query: '',
      selectedCategory: FILTER_KEYS.ALL,
      filterKeys: FILTER_KEYS,
    };
  },
  computed: {
    approvedTemplates() {
      return this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
        .filter(template => template.status?.toLowerCase() === 'approved');
    },
    customerTemplates() {
      return this.approvedTemplates.filter(
        template => !template.name.startsWith('sample_')
      );
    },
    sampleTemplates() {
      return this.approvedTemplates.filter(template =>
        template.name.startsWith('sample_')
      );
    },
    availableCategories() {
      const counts = {
        [FILTER_KEYS.ALL]: this.customerTemplates.length,
        [FILTER_KEYS.MARKETING]: 0,
        [FILTER_KEYS.UTILITY]: 0,
        [FILTER_KEYS.AUTHENTICATION]: 0,
        [FILTER_KEYS.SAMPLES]: this.sampleTemplates.length,
      };

      this.customerTemplates.forEach(template => {
        if (counts[template.category] !== undefined) {
          counts[template.category] += 1;
        }
      });

      return [
        {
          key: FILTER_KEYS.ALL,
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.ALL'),
          count: counts[FILTER_KEYS.ALL],
        },
        {
          key: FILTER_KEYS.MARKETING,
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.MARKETING'),
          count: counts[FILTER_KEYS.MARKETING],
        },
        {
          key: FILTER_KEYS.UTILITY,
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.UTILITY'),
          count: counts[FILTER_KEYS.UTILITY],
        },
        {
          key: FILTER_KEYS.AUTHENTICATION,
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.AUTHENTICATION'),
          count: counts[FILTER_KEYS.AUTHENTICATION],
        },
        {
          key: FILTER_KEYS.SAMPLES,
          label: this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.SAMPLES'),
          count: counts[FILTER_KEYS.SAMPLES],
        },
      ].filter(item => item.count > 0 || item.key === FILTER_KEYS.ALL);
    },
    filteredTemplateMessages() {
      const baseTemplates =
        this.selectedCategory === FILTER_KEYS.SAMPLES
          ? this.sampleTemplates
          : this.customerTemplates;

      return baseTemplates.filter(template => {
        const matchesSearch =
          !this.query ||
          template.name.toLowerCase().includes(this.query.toLowerCase()) ||
          this.getTemplateBody(template)
            .toLowerCase()
            .includes(this.query.toLowerCase());

        const matchesCategory =
          this.selectedCategory === FILTER_KEYS.ALL ||
          this.selectedCategory === FILTER_KEYS.SAMPLES ||
          template.category === this.selectedCategory;

        return matchesSearch && matchesCategory;
      });
    },
    selectedCategoryLabel() {
      return (
        this.availableCategories.find(item => item.key === this.selectedCategory)
          ?.label || this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.ALL')
      );
    },
  },
  methods: {
    getCategoryLabel(category) {
      if (!category) {
        return this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.ALL');
      }

      if (category === FILTER_KEYS.SAMPLES) {
        return this.$t('WHATSAPP_TEMPLATES.PICKER.FILTERS.SAMPLES');
      }

      const key = `WHATSAPP_TEMPLATES.PICKER.FILTERS.${category}`;
      const translated = this.$t(key);
      return translated === key ? category : translated;
    },
    getTemplateBody(template) {
      return (
        template.components?.find(component => component.type === 'BODY')?.text ||
        ''
      );
    },
    onSelectCategory(categoryKey) {
      this.selectedCategory = categoryKey;
    },
  },
};
</script>

<template>
  <div class="templates-picker">
    <aside class="templates-picker__sidebar">
      <p class="templates-picker__sidebar-title">
        {{ $t('WHATSAPP_TEMPLATES.PICKER.FILTERS.TITLE') }}
      </p>

      <button
        v-for="category in availableCategories"
        :key="category.key"
        class="templates-picker__sidebar-item"
        :class="{ active: selectedCategory === category.key }"
        @click="onSelectCategory(category.key)"
      >
        <span>{{ category.label }}</span>
        <span class="templates-picker__count">{{ category.count }}</span>
      </button>
    </aside>

    <section class="templates-picker__content">
      <div class="templates__list-search">
        <fluent-icon icon="search" class="search-icon" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="$t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="templates__search-input"
        />
      </div>

      <div class="templates-picker__results-meta">
        <span class="templates-picker__results-title">
          {{ selectedCategoryLabel }}
        </span>
        <span class="templates-picker__results-count">
          {{ filteredTemplateMessages.length }}
        </span>
      </div>

      <div class="template__list-container">
        <div
          v-for="(template, i) in filteredTemplateMessages"
          :key="`${template.name}-${template.language}-${i}`"
        >
          <button
            class="template__list-item"
            @click="$emit('onSelect', template)"
          >
            <div class="template__list-item-header">
              <div>
                <p class="label-title">
                  {{ template.name }}
                </p>
                <div class="template__meta-row">
                  <span class="template__meta-pill">
                    {{ getCategoryLabel(template.category) }}
                  </span>
                  <span class="template__meta-pill">
                    {{ template.language }}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p class="strong">
                {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.TEMPLATE_BODY') }}
              </p>
              <p class="label-body">{{ getTemplateBody(template) }}</p>
            </div>
          </button>
          <hr
            v-if="i !== filteredTemplateMessages.length - 1"
            :key="`hr-${i}`"
          />
        </div>

        <div v-if="!filteredTemplateMessages.length" class="templates-picker__empty">
          <p class="mb-1">
            {{ $t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
          </p>
          <strong>{{ query || selectedCategoryLabel }}</strong>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.templates-picker {
  @apply grid grid-cols-1 md:grid-cols-[14rem_minmax(0,1fr)] gap-4 w-full;
}

.templates-picker__sidebar {
  @apply rounded-lg border border-line-faint bg-surface-sunken p-3 h-fit;
}

.templates-picker__sidebar-title {
  @apply text-xs font-semibold uppercase tracking-wide text-subtle mb-3;
}

.templates-picker__sidebar-item {
  @apply flex items-center justify-between w-full rounded-lg px-3 py-2 text-sm text-left text-muted hover:bg-surface-hover mb-1;

  &.active {
    @apply bg-woot-50 text-woot-700 dark:bg-woot-900/30 dark:text-woot-100;
  }
}

.templates-picker__count {
  @apply inline-flex min-w-[1.5rem] justify-center rounded-full bg-surface-raised px-1.5 py-0.5 text-xs text-muted;
}

.templates-picker__content {
  @apply min-w-0;
}

.templates__list-search {
  @apply items-center flex bg-surface-sunken mb-2.5 py-0 px-2.5 rounded-md border border-solid border-line-faint;

  .search-icon {
    @apply text-subtle;
  }

  .templates__search-input {
    @apply bg-transparent border-0 text-xs h-9 m-0;
  }
}

.templates-picker__results-meta {
  @apply flex items-center justify-between mb-2;
}

.templates-picker__results-title {
  @apply text-sm font-semibold text-muted;
}

.templates-picker__results-count {
  @apply text-xs text-subtle;
}

.template__list-container {
  @apply bg-surface-sunken rounded-md max-h-[22rem] overflow-y-auto p-2.5;

  .template__list-item {
    @apply rounded-lg cursor-pointer block p-3 text-left w-full hover:bg-woot-50 dark:hover:bg-slate-800;
  }
}

.template__list-item-header {
  @apply flex items-start justify-between mb-3;
}

.label-title {
  @apply text-sm font-semibold text-body mb-1;
}

.template__meta-row {
  @apply flex flex-wrap gap-2;
}

.template__meta-pill {
  @apply inline-flex rounded-full bg-surface-raised px-2 py-1 text-[11px] text-muted;
}

.label-body {
  @apply text-sm text-muted;
  font-family: monospace;
}

.templates-picker__empty {
  @apply text-center text-sm text-muted py-8;
}

.strong {
  @apply text-xs font-semibold text-subtle mb-1 block;
}

hr {
  @apply border-b border-solid border-line-faint my-2.5 mx-auto max-w-[95%];
}
</style>
